import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Workspace } from "@/services/workspace-service";

interface WorkspaceState {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  workspacesLoaded: boolean; // Track if workspaces have been fetched from API
  setWorkspaces: (workspaces: Workspace[]) => void;
  setSelectedWorkspace: (
    workspace: Workspace | null,
  ) => boolean;
  clearWorkspace: (options?: { preserveSelection?: boolean }) => void;
}

const WORKSPACE_TTL_MS = 1000 * 60 * 60 * 8; // 8 hours
const TTL_SUFFIX = ":ttl";

function getExpiresAt(name: string, storage: Storage): number | null {
  const raw = storage.getItem(`${name}${TTL_SUFFIX}`);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function setExpiresAt(name: string, storage: Storage, expiresAt: number) {
  storage.setItem(`${name}${TTL_SUFFIX}`, String(expiresAt));
}

function clearExpiresAt(name: string, storage: Storage) {
  storage.removeItem(`${name}${TTL_SUFFIX}`);
}

function isExpired(expiresAt: number | null): boolean {
  return typeof expiresAt === "number" && Date.now() > expiresAt;
}

// Custom storage that uses sessionStorage for active workspace + localStorage for default
const hybridStorage = {
  getItem: (name: string): string | null => {
    // SSR/Node safety: storage APIs are only available in the browser.
    if (typeof window === "undefined") return null;

    // Try sessionStorage first (current tab's workspace)
    const sessionValue = sessionStorage.getItem(name);
    if (sessionValue) {
      const expiresAt = getExpiresAt(name, sessionStorage);
      if (isExpired(expiresAt)) {
        sessionStorage.removeItem(name);
        clearExpiresAt(name, sessionStorage);
        return null;
      }
      if (!expiresAt) {
        setExpiresAt(name, sessionStorage, Date.now() + WORKSPACE_TTL_MS);
      }
      return sessionValue;
    }

    // Fallback to localStorage (default workspace preference)
    const localValue = localStorage.getItem(name);
    if (localValue) {
      const expiresAt = getExpiresAt(name, localStorage);
      if (isExpired(expiresAt)) {
        localStorage.removeItem(name);
        clearExpiresAt(name, localStorage);
        return null;
      }
      if (!expiresAt) {
        setExpiresAt(name, localStorage, Date.now() + WORKSPACE_TTL_MS);
      }
      // Copy to sessionStorage for this tab
      sessionStorage.setItem(name, localValue);
      setExpiresAt(name, sessionStorage, Date.now() + WORKSPACE_TTL_MS);
      return localValue;
    }

    return null;
  },
  setItem: (name: string, value: string): void => {
    // SSR/Node safety: storage APIs are only available in the browser.
    if (typeof window === "undefined") return;

    const expiresAt = Date.now() + WORKSPACE_TTL_MS;
    // Store in both sessionStorage (for this tab) and localStorage (as default)
    sessionStorage.setItem(name, value);
    localStorage.setItem(name, value);
    setExpiresAt(name, sessionStorage, expiresAt);
    setExpiresAt(name, localStorage, expiresAt);
  },
  removeItem: (name: string): void => {
    // SSR/Node safety: storage APIs are only available in the browser.
    if (typeof window === "undefined") return;

    sessionStorage.removeItem(name);
    localStorage.removeItem(name);
    clearExpiresAt(name, sessionStorage);
    clearExpiresAt(name, localStorage);
  },
};

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      selectedWorkspace: null,
      workspacesLoaded: false,

      setWorkspaces: (workspaces) => {
        // Atomically set workspaces and reconcile selected workspace
        set((state) => {
          let nextSelected: Workspace | null = null;

          if (workspaces.length > 0) {
            if (state.selectedWorkspace) {
              const matched = workspaces.find(
                (w) =>
                  w.workspace_id === state.selectedWorkspace?.workspace_id &&
                  w.organization_id ===
                    state.selectedWorkspace?.organization_id,
              );
              nextSelected = matched ?? workspaces[0];
            } else {
              // Auto-select first workspace if none selected
              nextSelected = workspaces[0];
            }
          }

          return {
            workspaces,
            selectedWorkspace: nextSelected,
            workspacesLoaded: true,
          };
        });
      },

      setSelectedWorkspace: (workspace) => {
        const { workspaces, selectedWorkspace: current } = get();
        if (!workspace) {
          set({ selectedWorkspace: null });
          return true;
        }

        if (workspaces.length > 0) {
          const matched = workspaces.find(
            (w) => w.workspace_id === workspace.workspace_id,
          );
          if (!matched) {
            set({ selectedWorkspace: null });
            console.warn(
              `Tried to select non-existent workspace: ${workspace.workspace_id}`,
            );
            return false;
          }

          // Skip update if already selected (prevent unnecessary re-renders)
          if (current?.workspace_id === matched.workspace_id) {
            return true;
          }
          set({ selectedWorkspace: matched });
          return true;
        }

        set({ selectedWorkspace: workspace });
        return true;
      },

      clearWorkspace: (options) => {
        const preserveSelection = options?.preserveSelection ?? false;
        set((state) => ({
          workspaces: [],
          workspacesLoaded: false,
          selectedWorkspace: preserveSelection ? state.selectedWorkspace : null,
        }));
      },
    }),
    {
      name: "workspace-storage",
      storage: createJSONStorage(() => hybridStorage),
      // Only persist selectedWorkspace, not the full list
      partialize: (state) => ({
        selectedWorkspace: state.selectedWorkspace,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.workspaces = [];
          state.workspacesLoaded = false;
        }
      },
    },
  ),
);

export const getWorkspaceStoreState = () => useWorkspaceStore.getState();
